import './menu-item-cart-card.css';

import { Util } from '../../../utils/util';
import { IName } from '../../../interfaces/global.interfaces';
import { IOrderItem } from '../../../models/order.interface';
import { OnlineOrderingUtil } from '../../../online-ordering/online-ordering-util';
import { IDataItem } from '../../../models/data.interface';
import { IMenuItemCartCardUiParams, MenuItemCartCardBase } from './menu-item-cart-card-base';

interface IMenuItemCartCardSide {
    isWhole: boolean,
    isLeft: boolean,
    halfName: string,
    sideId: string,
    screenReaderKey: string,
    modifiers: string[]
}

/**
 * Class representing a cart card for menu items in an order.
 * Responsible for rendering and managing menu item cards in the cart.
 */
export class MenuItemCartCardMultiMode extends MenuItemCartCardBase {
    constructor(params: IMenuItemCartCardUiParams) {
        super(params);
    }

    protected override _afterCardRender(
        _item: IOrderItem,
        card: HTMLElement
    ): void {
        const buttons = Array.from(card.querySelectorAll(".details-toggle"));
        buttons.forEach(btn => {
            btn.addEventListener("click", () => this._handleDetailsOnClick(btn));
        });
    }

    /**
     * Generates the HTML for a menu item card in the cart.
     * @protected
     * @param {IOrderItem[]} items - The group of items to display in the card.
     * @returns {HTMLElement | null} - The generated HTML element for the item card, or null if the item is invalid.
     */
    protected _generateItemCardHtml(items: IOrderItem[]): HTMLElement | null {
        const firstHalfItem = items[0];
        const menuItem = this._data.getProperty("Items").find(x => x.ItemId === firstHalfItem.ItemId);
        if (!menuItem)
            return null;

        // 2DO: Points
        // let displayPoints = false;
        // const loyaltyPoints = displayPoints ? OnlineOrderingUtil.GetLoyaltyPoints(firstHalfItem.ItemId, firstHalfItem.SizeId, this._data.getBrandProperty("LoyaltyItems")) || 0 : 0;
        //if (this._loyaltyProvider.hasPointRedemption() && this._user.getProperty("LoyaltyData")) {
        //    const points = OnlineOrderingUtil.GetLoyaltyPoints(oItem.ItemId, oItem.SizeId, this._data.getProperty("LoyaltyItems"));
        //    if (points) {
        //        let innerText = "";
        //        const hidden = (oItem.IsRedeemed || points > (this._user.getProperty("LoyaltyData")?.CurrentPoints || 0));
        //        if (!hidden) {
        //            if (oItem.Quantity > 1)
        //                innerText = (points) ? `<span>${Names("Redeem")}</span>&nbsp1&nbsp<span>${Names("For")}</span>&nbsp${points}&nbsp<span>${Names("Points")}</span>` : "";
        //            else
        //                innerText = (points) ? `<span>${Names("RedeemFor")}</span>&nbsp${points}&nbsp<span>${Names("Points")}</span>` : "";

        //            const btnPnts =
        //                `<button id="btn_cart_item_redeem_${oItem.ItemKey}" name="btn_cart_item_redeem" class="${this._context.getProperty("buttonHoverStyle")} cart-item-redeem-btn" data-pts="${points}" data-iky="${oItem.ItemKey}">${innerText}</button>`;

        //            divItem.innerHTML += btnPnts;
        //        }
        //    }
        //}
        //}

        // 2DO: Comment
        //if (oItem.Comment !== "" && (oItem.HalfCount === 1 || (oItem.HalfCount === 2 && oItem.HalfIndex === 2)))
        //    divItem.innerHTML += `<div class="cart-item-row">${oItem.Comment}</div>`;

        // 2DO: Point stuff
        //if (displayPoints && (oItem.HalfCount === 1 || (oItem.HalfCount === 2 && oItem.HalfIndex === 2)))
        //    divItem.innerHTML += `<div style="text-align: center;">${loyaltyPoint} Points required</div>`;
        const isHalfHalf = firstHalfItem.HalfCount > 1;

        const header = this._generateHeaderTemplate(items, menuItem);
        const itemPoints = isHalfHalf ? this._generateItemPointsTemplate(items) : "";
        const { leftTemplate, rightTemplate } = this._generateItemsTemplate(items);
        const specialInstructions = this._generateInstructionTemplate(firstHalfItem);
        const taxes = this._generateTaxesTemplate(firstHalfItem);
        const footer = this._state.showOptions ? this.generateOptionsTemplate(firstHalfItem) : this._generateItemQuantityTemplate(firstHalfItem);

        const html = `
            <article class="menu-item-cart-card-parent" aria-labelledby="cart-item-title-${firstHalfItem.ItemKey}">
                ${header}
                ${itemPoints}
                <div class="menu-item-cart-card-body">
                    ${leftTemplate}
                    ${rightTemplate}
                </div>
                ${specialInstructions}
                ${taxes}
                ${footer}
            </article>`;

        const htmlElement = Util.createHtmlElementFromTemplate(html);

        this._addRedeemToggleButtonToCard(htmlElement, items);

        return htmlElement;
    }

    /**
     * Generates the header HTML for a cart item, including title, size,
     * crust, and total price.
     *
     * Handles half-and-half items and applies localization and currency
     * formatting based on store settings.
     *
     * @param items - Order items representing the same menu item.
     * @param menuItem - Menu item metadata.
     * @returns Rendered header HTML template.
     */
    protected _generateHeaderTemplate(items: IOrderItem[], menuItem: IDataItem): string {
        const firstHalfItem = items[0];
        const country = this._data.getProperty("StoreInfo").country;

        const isHalfHalf = firstHalfItem.HalfCount > 1;
        const name = this._localizationService.GetName(menuItem.Names);

        const price = this.calculateItemPrice(firstHalfItem, isHalfHalf);

        const title = isHalfHalf ? this._localizationService.names("HHP") : name;
        const singleModifiersAndSize = this._getSingleModifiersAndSize(firstHalfItem, menuItem);

        return `
            <header class="menu-item-cart-card">
                <div class="menu-item-cart-card-details-header">
                    <h3 class="item-title">${title}</h3>
                    <ul class="single-modifiers-list">${singleModifiersAndSize.join("")}</ul>
                </div>
                <div class="menu-item-cart-card-price">
                    <p>${Util.formatMoney(price * firstHalfItem.Quantity, country)}</p>
                </div>
            </header>`;
    }

    private _getSingleModifiersAndSize(item: IOrderItem, menuItem: IDataItem): string[] {
        const allDisplayGroups = this._data.getProperty("ModifierDisplayGroups");
        const modifiersData = this._data.getProperty("Modifiers");

        const modifiers = OnlineOrderingUtil._collectModifiersForDisplay([item]);

        let sizeName: IName[] = [];
        if (item.SizeId > 0) {
            const iSize = OnlineOrderingUtil.GetItemSize(menuItem, this._order.getProperty("OrderTypeID"), item.SizeId);
            const size = this._data.getProperty("Sizes").find(x => x.SizeId === iSize?.SizeId) || null;
            if (size)
                sizeName = size.Names;
        }

        const result: string[] = [`<li class="modifier">${this._localizationService.GetName(sizeName)}</li>`];
        for (const modifier of modifiers) {
            const displayGroup = allDisplayGroups.find(x => x.ModifierDisplayGroupId === modifier.displayGroupId);
            if (displayGroup?.IsHalfable)
                continue;

            const modifierData = modifiersData.find(x => x.ModifierId === modifier.modifierId);

            result.push(`<li class="modifier">${this._localizationService.GetName(modifierData?.Names ?? []).trim()}</li>`);
        }

        return result;
    }

    protected _generateItemPointsTemplate(items: IOrderItem[]): string {
        let innerText = "";
        if (
            this._loyaltyProvider.hasPointRedemption() &&
            this._profileService.user.getProperty("LoyaltyData")
        ) {
            const firstHalfPoints = OnlineOrderingUtil.GetLoyaltyPoints(
                items[0].ItemId,
                items[0].SizeId,
                this._data.getProperty("LoyaltyItems")
            );

            const secondHalfPoints =
                items.length > 1
                    ? OnlineOrderingUtil.GetLoyaltyPoints(
                        items[1].ItemId,
                        items[1].SizeId,
                        this._data.getProperty("LoyaltyItems")
                    )
                    : 0;

            const quantity = items[0].Quantity;
            const pointsUnitText = this._localizationService.getLoyaltyUnitName(this._data.getLoyaltyInfo());

            const points = Math.max(firstHalfPoints ?? 0, secondHalfPoints ?? 0) * quantity;
            const pointsText = points.toLocaleString(this._context.getProperty("languageCode"));

            const pointsFullText = this._localizationService.names("POINTS_LABEL_WITH_POINTS")
                .replaceAll("{{points}}", pointsText)
                .replaceAll("{{points_unit}}", pointsUnitText);

            const ariaLabelText = this._localizationService.names("POINTS_REQUIRED_LABEL")
                .replaceAll("{{points}}", pointsText)
                .replaceAll("{{points_unit}}", pointsUnitText);

            if (points) {
                innerText = `
                    <div class="menu-item-cart-card-points-container">
                        <span class="menu-item-cart-card-badge menu-item-cart-card-points" aria-label="${ariaLabelText}">${pointsFullText}</span>
                        ${items[0].IsRedeemed ? `<span class="menu-item-cart-card-badge menu-item-cart-card-redeemed-label">${this._localizationService.names("Redeemed")}</span>` : ""}
                    </div>`;
            }
        }
        return innerText;
    }

    /**
     * Generates HTML templates for left and right items in a half-and-half order.
     *
     * Collects modifiers, resolves item metadata, and renders the corresponding
     * markup for each side (left / right), including images, titles, modifiers,
     * and accessibility attributes.
     *
     * @param items - Order items to render.
     * @returns An object containing the rendered HTML templates for each side.
     */
    private _generateItemsTemplate(items: IOrderItem[]): { leftTemplate: string, rightTemplate: string } {
        const modifiers = OnlineOrderingUtil._collectModifiersForDisplay(items);

        const modifiersTextData = OnlineOrderingUtil.generateItemModifiersNew(
            modifiers,
            this._data,
            this._localizationService);

        const { leftModifiers, rightModifiers } = this._renderModifierListItems(modifiersTextData.left, modifiersTextData.right);

        let leftTemplate = "";
        let rightTemplate = "";

        for (const item of items) {
            const menuItem = this._data.getProperty("Items").find(x => x.ItemId === item.ItemId);
            if (!menuItem)
                continue;

            const side = this._getSideMeta(item.HalfIndex, leftModifiers, rightModifiers);

            const html = this._itemHtmlTemplate(item, menuItem, side);

            if (side.isLeft)
                leftTemplate = html;
            else
                rightTemplate = html;
        }

        return { leftTemplate, rightTemplate };
    }

    private _itemHtmlTemplate(item: IOrderItem, menuItem: IDataItem, side: IMenuItemCartCardSide): string {
        const name = this._localizationService.GetName(menuItem.Names);
        const calories = this.getItemCalories(menuItem, item);
        const hasImage = Boolean(menuItem.ImageURL);
        const itemPoints = side.isWhole ? this._generateItemPointsTemplate([item]) : "";

        return `
            <article class="menu-item-cart-card" aria-labelledby="${side.halfName}-title-${item.ItemKey}">
                ${hasImage ?
                `<figure class="menu-item-cart-card-image">
                        <img src="${menuItem.ImageURL}" alt="${name}">
                    </figure>` : ""
            }
                <div class="menu-item-cart-card-details">
                    ${side.isWhole ? itemPoints :
                `<div class="half-item-title">
                            <span class="icon-item-half-${side.sideId}" aria-hidden="true"></span>
                            <span class="sr-only">${this._localizationService.names(side.screenReaderKey)}</span>
                            <h4 id="${side.halfName}-title-${item.ItemKey}" class="item-title">${name}</h4>
                        </div>`
            }
                    ${this._state.keepExpanded ? "" :
                `<button class="details-toggle" aria-expanded="false" aria-controls="details-${side.halfName}-${item.ItemKey}">
                            <span class="toggle-text">${this._localizationService.names("VIEW_DETAILS")}</span>
                            <i class="icon-arrow-back" aria-hidden="true"></i>
                        </button>`
            }
                    <div id="details-${side.halfName}-${item.ItemKey}" class="modifiers-info ${this._state.keepExpanded ? "expanded" : ""}">
                        ${calories.length ? `<p class="item-calories">${this._localizationService.GetName(calories)}</p>` : ""}
                        <ul class="modifiers-list">
                            ${side.modifiers.join("")}
                        </ul>
                    </div>
                </div>
            </article>`;
    }

    private _getSideMeta(halfIndex: number, leftModifiers: string[], rightModifiers: string[]): IMenuItemCartCardSide {
        const SIDE_META = {
            0: {
                isWhole: true,
                isLeft: true,
                halfName: "whole",
                sideId: "left",
                screenReaderKey: "ARIA_LEFT_HALF"
            },
            1: {
                isWhole: false,
                isLeft: true,
                halfName: "left-half",
                sideId: "left",
                screenReaderKey: "ARIA_LEFT_HALF"
            },
            2: {
                isWhole: false,
                isLeft: false,
                halfName: "right-half",
                sideId: "right",
                screenReaderKey: "ARIA_RIGHT_HALF"
            }
        } as const;

        const meta = SIDE_META[halfIndex as 0 | 1 | 2] ?? SIDE_META[0];

        return {
            ...meta,
            modifiers: meta.isLeft || meta.isWhole ? leftModifiers : rightModifiers
        };
    };

    /**
     * Converts modifier text into rendered HTML list items for left and right sides.
     *
     * Default and non-default modifiers are rendered with different CSS classes
     * to support visual distinction.
     *
     * @param left - Left side modifiers grouped by default and non-default.
     * @param right - Right side modifiers grouped by default and non-default.
     * @returns Rendered HTML list items for left and right sides.
     */
    private _renderModifierListItems(
        left: { default: string[], nonDefault: string[] },
        right: { default: string[], nonDefault: string[] }
    ): { leftModifiers: string[], rightModifiers: string[] } {
        const leftModifiers = [] as string[];
        const rightModifiers = [] as string[];

        for (const modifier of left.default) {
            leftModifiers.push(`<li class="modifier">${modifier}</li>`);
        }

        for (const modifier of left.nonDefault) {
            leftModifiers.push(`<li class="modifier-non-default">${modifier}</li>`);
        }

        for (const modifier of right.default) {
            rightModifiers.push(`<li class="modifier">${modifier}</li>`);
        }

        for (const modifier of right.nonDefault) {
            rightModifiers.push(`<li class="modifier-non-default">${modifier}</li>`);
        }

        return { leftModifiers, rightModifiers };
    }

    /**
     * Generates the HTML template for special instructions on an order item.
     *
     * Returns an empty string when no instructions are present.
     *
     * @param item - Order item containing special instructions.
     * @returns Rendered instructions HTML or an empty string.
     */
    protected _generateInstructionTemplate(item: IOrderItem): string {
        const instructions = this._generateInstructionList(item);
        if (instructions.length === 0)
            return "";

        return `
            <section class="menu-item-cart-card-instructions" aria-labelledby="instructions-label-${item.ItemKey}">
                <span id="instructions-label-${item.ItemKey}">${this._localizationService.names("SPECIAL_INSTRUCTIONS")}:</span>
                <ul class="instructions-list">
                    ${instructions.join("")}
                </ul>
            </section>`;
    }

    /**
     * Generates the HTML for special instructions for the item.
     * @protected
     * @param {IOrderItem} item - The specific order item.
     * @returns {string} - The HTML for special instructions.
     */
    protected _generateInstructionList(item: IOrderItem): string[] {
        const instructions = item.Instructions;
        if (!instructions?.length)
            return [];

        const instructionsData = this._data.getProperty("Instructions");

        // Pre-index by CommentId (O(n))
        const lookup = new Map<number, typeof instructionsData[number]>();
        for (const instruction of instructionsData) {
            lookup.set(instruction.CommentId, instruction);
        }

        const result: string[] = [];
        for (const instruction of instructions) {
            const data = lookup.get(instruction.commentId);
            if (!data) continue;

            result.push(
                `<li>${this._localizationService.GetNewName(data.Names)}</li>`
            );
        }

        return result;
    }

    /**
     * Generates the HTML for taxes displayed next to the item.
     * @private
     * @param {IOrderItem} item - The specific order item.
     * @returns {string} - The HTML for taxes.
     */
    protected _generateTaxesTemplate(item: IOrderItem): string {
        const taxes = item.Taxes;
        if (!taxes?.length)
            return "";

        const orderTypeId = this._order.getProperty("OrderTypeID");

        // Pre-index Taxes by TaxId (O(n))
        const taxesData = this._data.getProperty("Taxes");
        const taxLookup = new Map<number, typeof taxesData[number]>();
        for (const tax of taxesData) {
            taxLookup.set(tax.TaxId, tax);
        }

        const taxList: string[] = [];
        for (const tax of taxes) {
            if (!tax.DisplayNextToItem || !tax.TaxTypeName)
                continue;

            const taxData = taxLookup.get(tax.TaxId);
            if (taxData?.OrderTypesExcluded.includes(orderTypeId))
                continue;

            const taxNames =
                typeof tax.TaxTypeName === "string"
                    ? JSON.parse(tax.TaxTypeName) as IName[]
                    : tax.TaxTypeName;

            taxList.push(
                `<li>${this._localizationService.GetName(this.localizeTaxNames(taxNames, tax.Amount))}</li>`
            );
        }

        if (taxList.length === 0)
            return "";

        return `
            <section class="menu-item-cart-card-taxes" aria-labelledby="item-taxes-label-1">
              <span id="item-taxes-label-1" class="taxes-label">${this._localizationService.names("TAXES")}:</span>

              <ul class="taxes-list">
                ${taxList.join("")}
              </ul>
            </section>`;
    }

    /**
     * Handles the expand/collapse behavior for the item details section.
     *
     * Toggles the `aria-expanded` state, updates the localized toggle text,
     * and applies the corresponding expanded CSS class.
     *
     * @param button - The details toggle button element.
     */
    private readonly _handleDetailsOnClick = (button: Element): void => {
        const details = button.nextElementSibling;
        const expanded = button.getAttribute('aria-expanded') === 'true';

        button.setAttribute('aria-expanded', String(!expanded));
        const text = button.querySelector('.toggle-text');
        if (text) {
            const option = expanded ? 'VIEW_DETAILS' : 'HIDE_DETAILS'
            text.textContent = this._localizationService.names(option, this._context.getProperty("languageCode"));
        }

        if (details)
            details.classList.toggle('expanded', !expanded);
    }
}